import React, { useState } from 'react';
import { Container, Paper, TextField, Button, Typography, Box, Alert, IconButton, InputAdornment, LinearProgress, Link as MuiLink } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

function passwordStrength(pw) {
  if (!pw) return { score: 0, label: '', color: '#e0e0e0' };
  let score = 0;
  if (pw.length >= 8) score += 25;
  if (pw.length >= 12) score += 15;
  if (/[A-Z]/.test(pw)) score += 15;
  if (/[a-z]/.test(pw)) score += 10;
  if (/[0-9]/.test(pw)) score += 15;
  if (/[^A-Za-z0-9]/.test(pw)) score += 20;
  score = Math.min(100, score);
  if (score < 40) return { score, label: 'Weak', color: '#c62828' };
  if (score < 70) return { score, label: 'Fair', color: '#ef9f27' };
  if (score < 90) return { score, label: 'Good', color: '#0288d1' };
  return { score, label: 'Strong', color: '#2e7d32' };
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const pw = passwordStrength(password);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) { setError('Password must include at least one letter and one number'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setError(''); setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed. The link may have expired.');
    } finally { setSubmitting(false); }
  };

  if (!token) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 5, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a237e', mb: 1 }}>Yaplar</Typography>
          <Alert severity="error" sx={{ my: 3 }}>This link is invalid or missing a token.</Alert>
          <Button variant="contained" component={Link} to="/forgot-password" sx={{ bgcolor: '#1a237e', borderRadius: 2 }}>
            Request a new link
          </Button>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 5, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a237e', mb: 1 }}>Yaplar</Typography>
          <Box sx={{ fontSize: 64, my: 2 }}>✅</Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Password reset</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>Redirecting you to sign in…</Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper sx={{ p: 5, borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a237e', mb: 0.5 }}>Yaplar</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>Choose a new password</Typography>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <form onSubmit={submit}>
          <TextField label="New password" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
            fullWidth required size="small" sx={{ mb: 1 }} autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPw(!showPw)}>{showPw ? <VisibilityOff /> : <Visibility />}</IconButton>
                </InputAdornment>
              ),
            }}
            helperText="Minimum 8 characters, including a letter and a number"
          />
          {password && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#666' }}>Strength</Typography>
                <Typography variant="caption" sx={{ color: pw.color, fontWeight: 700 }}>{pw.label}</Typography>
              </Box>
              <LinearProgress variant="determinate" value={pw.score} sx={{ height: 6, borderRadius: 3, bgcolor: '#eceff1', '& .MuiLinearProgress-bar': { bgcolor: pw.color } }} />
            </Box>
          )}
          <TextField label="Confirm new password" type={showPw ? 'text' : 'password'} value={confirm} onChange={e => setConfirm(e.target.value)}
            fullWidth required size="small" sx={{ mb: 3 }}
            error={confirm && confirm !== password}
            helperText={confirm && confirm !== password ? 'Passwords do not match' : ''}
          />
          <Button type="submit" variant="contained" fullWidth size="large" disabled={submitting}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, py: 1.5, mb: 2, fontWeight: 700, '&:hover': { bgcolor: '#283593' } }}>
            {submitting ? 'Resetting…' : 'Reset password'}
          </Button>
          <Typography variant="body2" sx={{ textAlign: 'center', color: '#666' }}>
            <MuiLink component={Link} to="/login" sx={{ color: '#1a237e', fontWeight: 600 }}>Back to sign in</MuiLink>
          </Typography>
        </form>
      </Paper>
    </Container>
  );
}
